import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertAmountToWords, roundToNearestInteger } from "./utility";

function mapSeller(s) {
    return {
        name: s?.companyName || s?.name || "",
        subtitle: s?.companySubtitle || s?.subtitle || "",
    };
}

function buildFooterLines(fromSeller) {
    const s = fromSeller || {};
    const address = s.companyAddress || s.address || null;
    const phones = [s.companyPhone1 || s.phone1, s.companyPhone2 || s.phone2].filter(Boolean).join(" - ");
    const email = s.companyEmail || s.email || null;
    const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

    const regs = [
        s.companyIce || s.ice ? `ICE: ${s.companyIce || s.ice}` : null,
        s.companyRc || s.rc ? `RC: ${s.companyRc || s.rc}` : null,
        s.companyPatente || s.patente ? `Patente: ${s.companyPatente || s.patente}` : null,
        s.companyIf || s.if ? `IF: ${s.companyIf || s.if}` : null,
        s.companyCnss || s.cnss ? `CNSS: ${s.companyCnss || s.cnss}` : null,
    ].filter(Boolean).join(" - ");

    return { line1, regs };
}

export const generateRetourFournisseur = (
    retour,
    {
        company = {},
        docType = "RETOUR FOURNISSEUR",
        headerTitle,
        headerSubtitle,
        showPrices = true,
        priceMode = "TTC",
        format = "A4",
    } = {}
) => {
    const doc = new jsPDF({ unit: "mm", format: format.toLowerCase() });
    const seller = mapSeller(company);
    const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(company);

    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;

    const M = format === "A4" ? 20 : 12;
    const FOOTER_H = format === "A4" ? 22 : 18;

    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
        totalBg: [163, 240, 183],
    };

    const fmtMoney = (v) =>
        String(roundToNearestInteger(v)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const frDate = (d) =>
        d ? new Date(d).toLocaleDateString("fr-FR") : "-";

    const amountWords = (n) =>
        convertAmountToWords(n).toUpperCase();

    const resolvedHeaderTitle =
        headerTitle ||
        company.documentHeader ||
        seller.name ||
        "MA SOCIETE";

    const resolvedHeaderSubtitle =
        headerSubtitle ||
        seller.subtitle ||
        company.tagline ||
        "";

    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 22 : 18);
        doc.setTextColor(...BRAND.primary);

        const wrappedTitle = doc.splitTextToSize(String(resolvedHeaderTitle), PAGE_W - M * 2);
        doc.text(wrappedTitle, PAGE_W / 2, M, { align: "center" });
        const titleBottomY = M + (wrappedTitle.length - 1) * (format === "A4" ? 8 : 7);

        if (resolvedHeaderSubtitle) {
            doc.setFontSize(format === "A4" ? 10 : 9);
            doc.setTextColor(...BRAND.subtle);
            const wrappedSubtitle = doc.splitTextToSize(String(resolvedHeaderSubtitle), PAGE_W - M * 2);
            doc.text(wrappedSubtitle, PAGE_W / 2, titleBottomY + 7, { align: "center" });
            const subtitleBottomY = titleBottomY + 7 + (wrappedSubtitle.length - 1) * (format === "A4" ? 4.5 : 4);
            doc.setDrawColor(...BRAND.line);
            doc.line(M, subtitleBottomY + 5, PAGE_W - M, subtitleBottomY + 5);
            return subtitleBottomY + 5;
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, titleBottomY + 5, PAGE_W - M, titleBottomY + 5);
        return titleBottomY + 5;
    };

    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_MIN_H = format === "A4" ? 45 : 35;
    let CARD_Y = M + 16;
    const CARD_LINE_H = format === "A4" ? 6 : 5;
    const CARD_CONTENT_W = CARD_W - 10;

    const normalizeCardLines = (lines) => {
        return (lines || []).flatMap((line) => {
            if (!line) return [];
            return doc.splitTextToSize(String(line), CARD_CONTENT_W);
        });
    };

    const getCardHeight = (lines) => {
        const contentLines = normalizeCardLines(lines);
        const contentHeight = 14 + Math.max(contentLines.length - 1, 0) * CARD_LINE_H + 6;
        return Math.max(CARD_MIN_H, contentHeight);
    };

    const drawCard = (x, title, lines, cardHeight) => {
        doc.setDrawColor(0);
        doc.roundedRect(x, CARD_Y, CARD_W, cardHeight, 3, 3);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 11 : 9);
        doc.setTextColor(...BRAND.ink);
        doc.text(title, x + 5, CARD_Y + 7);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(format === "A4" ? 9 : 8);
        doc.setTextColor(...BRAND.subtle);

        normalizeCardLines(lines).forEach((line, i) => {
            doc.text(String(line), x + 5, CARD_Y + 14 + i * CARD_LINE_H);
        });
    };

    const headers = showPrices
        ? ["#", "Article", "Qte", "UM", priceMode === "HT" ? "PU HT" : "PU TTC", priceMode === "HT" ? "Total HT" : "Total TTC"]
        : ["#", "Article", "Qte", "UM"];

    const rows = (retour.lignes || []).map((l, i) => {
        const qt = l.quantiteRetournee;
        const pu = priceMode === "HT" ? l.prixUnitaireHT : l.prixUnitaireTTC;
        const tot = priceMode === "HT" ? qt * l.prixUnitaireHT : qt * l.prixUnitaireTTC;

        return showPrices
            ? [i + 1, l.articleNom, qt, l.unite, fmtMoney(pu), fmtMoney(tot)]
            : [i + 1, l.articleNom, qt, l.unite];
    });

    const headerBottomY = drawHeader();
    CARD_Y = headerBottomY + 4;

    const retourLines = [
        `N° : ${retour.numeroRetour || "-"}`,
        `Date : ${frDate(retour.dateRetour)}`,
        retour.transporteurName ? `Transporteur : ${retour.transporteurName}` : null,
    ];

    const fournisseurLines = [
        retour.fournisseurNom,
        retour.fournisseurVille,
        `Tel : ${retour.fournisseurTelephone || "-"}`,
    ];

    const cardsHeight = Math.max(getCardHeight(retourLines), getCardHeight(fournisseurLines));
    const tableY = CARD_Y + cardsHeight + (format === "A4" ? 10 : 6);

    drawCard(M, docType, retourLines, cardsHeight);
    drawCard(M * 2 + CARD_W, "FOURNISSEUR", fournisseurLines, cardsHeight);

    autoTable(doc, {
        startY: tableY,
        head: [headers],
        body: rows,
        styles: { fontSize: format === "A4" ? 9 : 7, cellPadding: format === "A4" ? 3 : 2 },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        tableLineColor: BRAND.line,
        tableLineWidth: 0.2,
    });

    const drawTotals = (y) => {
        const boxW = format === "A4" ? 70 : 60;
        const x = PAGE_W - M - boxW;

        doc.setFillColor(...BRAND.totalBg);
        doc.roundedRect(x, y, boxW, format === "A4" ? 24 : 18, 3, 3, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 11 : 9);
        doc.setTextColor(...BRAND.ink);

        doc.text("TOTAL TTC", x + 5, y + 9);
        doc.text(fmtMoney(retour.montantTTC), x + boxW - 5, y + (format === "A4" ? 18 : 13), {
            align: "right",
        });
    };

    const yAfter = doc.lastAutoTable.finalY + (format === "A4" ? 10 : 6);

    if (showPrices) {
        drawTotals(yAfter);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(format === "A4" ? 9 : 8);
        doc.setTextColor(...BRAND.subtle);
        doc.text(
            `Arrete a la somme de : ${amountWords(retour.montantTTC)}`,
            M,
            yAfter + (format === "A4" ? 32 : 24)
        );
    }

    doc.setDrawColor(0);
    doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

    doc.setFontSize(format === "A4" ? 8 : 7);
    doc.setTextColor(80);

    if (footerLine1) {
        doc.text(footerLine1, PAGE_W / 2, PAGE_H - 11, { align: "center" });
    }
    if (footerLine2) {
        doc.text(footerLine2, PAGE_W / 2, PAGE_H - 6, { align: "center" });
    }

    return doc;
};
